import { FunnelIcon } from "@heroicons/react/24/outline";
import { CustomListItemId } from "@sp-crm/core";
import { StatelessSearchbarItem } from "components/community-search/search-bar/stateless-searchbar-item";
import { Checkbox } from "components/ui/checkbox";
import { Radio } from "components/ui/radio";
import React from "react";
import {
    activeCategoryItemIds,
    ClientStatusFilterMode,
    ClientStatusFilterValue,
    closedWonItem,
    HomeCareStatusList,
} from "./status-groups";

interface ClientStatusFilterProps {
    customList: HomeCareStatusList;
    value: ClientStatusFilterValue;
    onChange: (value: ClientStatusFilterValue) => void;
}

const modeOptions: { mode: ClientStatusFilterMode; label: string }[] = [
    { mode: "currentClients", label: "Current clients" },
    { mode: "activeLeads", label: "Active leads" },
];

const triggerLabel = (mode: ClientStatusFilterMode): string => {
    const option = modeOptions.find(o => o.mode === mode);
    return `Status: ${option?.label ?? mode}`;
};

export const ClientStatusFilter: React.FC<ClientStatusFilterProps> = props => {
    const { customList, value, onChange } = props;

    const activeIds = activeCategoryItemIds(customList);
    const activeItems = (customList?.items ?? []).filter(item =>
        activeIds.includes(item.id),
    );
    const wonItem = closedWonItem(customList);
    const displayedItems =
        value.mode === "currentClients" && wonItem
            ? [...activeItems, wonItem]
            : activeItems;

    const selectMode = (mode: ClientStatusFilterMode) => {
        if (mode === "currentClients") {
            onChange({ mode: "currentClients" });
            return;
        }
        onChange({ mode: "activeLeads", statusIds: activeIds });
    };

    const check = (id: CustomListItemId) => {
        if (value.mode !== "activeLeads" || value.statusIds.includes(id)) {
            return;
        }
        onChange({ mode: "activeLeads", statusIds: [...value.statusIds, id] });
    };

    const uncheck = (id: CustomListItemId) => {
        if (value.mode !== "activeLeads" || value.statusIds.length <= 1) {
            return;
        }
        onChange({
            mode: "activeLeads",
            statusIds: value.statusIds.filter(v => v !== id),
        });
    };

    const isChecked = (id: CustomListItemId): boolean =>
        value.mode === "currentClients" ? true : value.statusIds.includes(id);

    return (
        <StatelessSearchbarItem
            content={
                <div className="flex items-center space-x-2">
                    <FunnelIcon className="w-5 h-5 flex-shrink-0" />
                    <span className="text-sm">{triggerLabel(value.mode)}</span>
                </div>
            }>
            <div className="w-64 p-1 space-y-3">
                <div className="space-y-2">
                    {modeOptions.map(option => (
                        <Radio
                            key={option.mode}
                            name="client-status-mode"
                            checked={value.mode === option.mode}
                            onChange={() => selectMode(option.mode)}
                            label={option.label}
                        />
                    ))}
                </div>
                <div className="border-t border-gray-200 pt-3 space-y-2">
                    {displayedItems.map(item => (
                        <Checkbox
                            key={item.id}
                            checked={isChecked(item.id)}
                            disabled={value.mode === "currentClients"}
                            onChange={e =>
                                e.target.checked ? check(item.id) : uncheck(item.id)
                            }
                            label={item.name}
                        />
                    ))}
                </div>
            </div>
        </StatelessSearchbarItem>
    );
};
